import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const ForgotPassword = () => {
    const { t } = useLanguage();
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [debugLink, setDebugLink] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');
        setDebugLink('');

        try {
            console.log('Sending request with email:', email);
            const response = await api.post('/forgot-password.php', { email });
            console.log('Response:', response);

            setMessage(response.data.message);

            // Show debug link if in development
            if (response.data.debug_link) {
                setDebugLink(response.data.debug_link);
            }

            setEmail('');
        } catch (err) {
            console.error('Error details:', err);
            console.error('Error response:', err.response);
            setError(err.response?.data?.message || t('auth.forgotPasswordFlow.error', 'An error occurred'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        {t('auth.forgotPasswordFlow.title', 'Forgot Password')}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        {t('auth.forgotPasswordFlow.subtitle', "Enter your email address and we'll send you a link to reset your password.")}
                    </p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <label htmlFor="email" className="sr-only">
                                {t('auth.forgotPasswordFlow.emailLabel', 'Email address')}
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                placeholder={t('auth.forgotPasswordFlow.emailPlaceholder', 'Email address')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="text-red-500 text-sm text-center bg-red-50 p-3 rounded">
                            {t('common.error', 'Error')}: {error}
                        </div>
                    )}

                    {message && (
                        <div className="text-green-500 text-sm text-center bg-green-50 p-3 rounded">
                            {message}
                        </div>
                    )}

                    {debugLink && (
                        <div className="text-blue-500 text-sm text-center bg-blue-50 p-3 rounded break-all">
                            <p className="font-bold mb-2">{t('auth.forgotPasswordFlow.debugLink', 'Debug Link (click to reset):')}</p>
                            <a
                                href={debugLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline hover:text-blue-800"
                            >
                                {debugLink}
                            </a>
                        </div>
                    )}

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {loading ? t('auth.forgotPasswordFlow.sending', 'Sending...') : t('auth.forgotPasswordFlow.sendSubmit', 'Send Reset Link')}
                        </button>
                    </div>

                    <div className="text-center">
                        <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">
                            {t('auth.forgotPasswordFlow.backToLogin', 'Back to Login')}
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ForgotPassword;